/* eslint-disable */
import _ from 'lodash';
import { TUnitType } from '../api/analytics/UnitType';

import { DataframeDataRetrievalResponse, DimSettings } from '../api/types';
import { ChartSeries } from '../types/charts/chart-options';
import { DraggableMenuItemData } from '../types/databuilder/databuilder';
import { IIndexable } from '../types/general';
import {
	formatClusteredBarData,
	formatClusteredColumnData,
	formatDefaultMultiSeriesData,
	formatHeatmapData,
	formatSankeyOrDepWheelMultiSeriesData,
	formatTreemapStripesData,
} from './chart.multiSeries.helpter';
import {
	getBulletGraphData,
	getColumnRangeData,
	getDefaultSingleSeriesData,
	getOdometerData,
	getTreemapSquarifiedData,
} from './chart.singleSeries.helper';
import { MultipartResponse } from './multipart-response.helper';
import { PreviewGrandTotal } from '../types/charts/preview-grand-total';
import { getNumberByKey } from './general-helper-functions';

export type ChartDataResponse = {
	xAxisArray: string[];
	yAxisArray: string[];
	valueArray: string[];
	valueArray2: string[];
	seriesData: ChartSeries[];
	lookupData: (string | number)[][];
};

export const getChartSeriesData = (
	chartRetrevialData: IIndexable[],
	dimConfigValues: DimSettings[],
	enabledDimValueIds: number[],
	overrideValueDefinedColors: boolean,
	isSingleSeries: boolean,
	chartType?: string,
	unitType?: TUnitType,
	selected2ndUnitType?: TUnitType,
	selectedNumDecimals?: number,
	droppedFactsCount?: number,
	chartColor?: string,
	chartColor2?: string,
	grandTotal?: [{ [key: string]: number }],
	showPercentages?: boolean,
	droppedFacts?: DraggableMenuItemData[]
) => {
	let response: ChartDataResponse = {
		xAxisArray: [],
		yAxisArray: [],
		seriesData: [],
		valueArray: [],
		valueArray2: [],
		lookupData: [[], []],
	};
	let numberOfDecimals = selectedNumDecimals ? selectedNumDecimals : 2;
	let ignoreChartColor = false;

	//multi-series
	if (!isSingleSeries) {
		numberOfDecimals = (
			showPercentages
				? 2
				: droppedFacts && droppedFacts?.length > 0
				? droppedFacts[0]?.data?.numDecimals
				: selectedNumDecimals ?? 2
		) as number;

		switch (chartType) {
			case 'clusteredbar':
				response = formatClusteredBarData(
					chartRetrevialData,
					unitType,
					numberOfDecimals,
					grandTotal,
					showPercentages
				);
				break;
			case 'clusteredline':
			case 'clusteredcolumn':
				response = formatClusteredColumnData(
					chartRetrevialData,
					unitType,
					numberOfDecimals,
					grandTotal,
					showPercentages
				);
				break;
			case 'heatmap':
				response = formatHeatmapData(
					chartRetrevialData,
					unitType,
					numberOfDecimals,
					grandTotal,
					showPercentages
				);
				break;
			case 'sankey':
			case 'dependencywheel':
				response = formatSankeyOrDepWheelMultiSeriesData(
					chartRetrevialData,
					unitType,
					numberOfDecimals,
					grandTotal
				);
				break;
			case 'treemap':
			case 'treemapstripes':
				response = formatTreemapStripesData(
					chartRetrevialData,
					unitType,
					numberOfDecimals,
					grandTotal,
					showPercentages
				);
				break;
			case 'bullet':
				response = getBulletGraphData(
					chartRetrevialData,
					unitType,
					selected2ndUnitType,
					numberOfDecimals,
					droppedFactsCount,
					chartColor,
					grandTotal,
					showPercentages,
					droppedFacts,
					chartType,
					true
				);
				break;
			default:
				response = formatDefaultMultiSeriesData(
					chartRetrevialData,
					unitType,
					numberOfDecimals,
					grandTotal,
					showPercentages
				);
				break;
		}
	}
	//normal single-series
	else {
		switch (chartType) {
			case 'odometer':
				response = getOdometerData(
					chartRetrevialData,
					unitType,
					numberOfDecimals,
					grandTotal,
					showPercentages
				);
				break;
			case 'columnrange':
				response = getColumnRangeData(
					chartRetrevialData,
					unitType,
					numberOfDecimals,
					chartColor,
					grandTotal,
					showPercentages,
					droppedFacts
				);
				break;
			case 'treemapsquarified':
				response = getTreemapSquarifiedData(
					chartRetrevialData,
					unitType,
					numberOfDecimals,
					grandTotal,
					showPercentages,
					droppedFacts
				);
				break;
			case 'bullet':
				response = getBulletGraphData(
					chartRetrevialData,
					unitType,
					selected2ndUnitType,
					numberOfDecimals,
					droppedFactsCount,
					chartColor,
					grandTotal,
					showPercentages,
					droppedFacts,
					chartType,
					false
				);
				break;
			default:
				response = getDefaultSingleSeriesData(
					chartRetrevialData,
					unitType,
					selected2ndUnitType,
					numberOfDecimals,
					droppedFactsCount,
					chartColor,
					grandTotal,
					showPercentages,
					droppedFacts,
					chartType
				);
		}
	}

	if (chartType === 'pie' || chartType === 'donut') {
		ignoreChartColor = true;
	}

	if (dimConfigValues.length) {
		const filteredDimValues = dimConfigValues.filter((val) =>
			enabledDimValueIds.includes(+val.entity_id)
		);

		// handle sequencing
		if (filteredDimValues.length) {
			response.xAxisArray = response.xAxisArray.sort((a, b) => {
				const formattedValA = filteredDimValues
					.flatMap((filterDim) => filterDim.values)
					.find((obj) => obj.value === a);
				const formattedValB = filteredDimValues
					.flatMap((filterDim) => filterDim.values)
					.find((obj) => obj.value === b);

				const valueA = formattedValA ? +formattedValA.sequence : 0;
				const valueB = formattedValB ? +formattedValB.sequence : 0;

				return valueA - valueB;
			});

			if (isSingleSeries) {
				response.seriesData.forEach((seriesItem) => {
					seriesItem.data = seriesItem.data.sort((a, b) => {
						const formattedValA = filteredDimValues
							.flatMap((filterDim) => filterDim.values)
							// eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
							.find((obj) => obj.value === (a as any).name);
						const formattedValB = filteredDimValues
							.flatMap((filterDim) => filterDim.values)
							// eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
							.find((obj) => obj.value === (b as any).name);

						const valueA = formattedValA ? +formattedValA.sequence : 0;
						const valueB = formattedValB ? +formattedValB.sequence : 0;

						return valueA - valueB;
					});
				});
			} else {
				response.seriesData = response.seriesData.sort((a, b) => {
					const formattedValA = filteredDimValues
						.flatMap((filterDim) => filterDim.values)
						.find((obj) => obj.value === a.name);
					const formattedValB = filteredDimValues
						.flatMap((filterDim) => filterDim.values)
						.find((obj) => obj.value === b.name);

					const valueA = formattedValA ? +formattedValA.sequence : 0;
					const valueB = formattedValB ? +formattedValB.sequence : 0;

					return valueA - valueB;
				});
			}
		}

		// handle colors
		if (isSingleSeries) {
			if (!overrideValueDefinedColors) {
				response.seriesData.forEach((item, idx) => {
					item.color = undefined;
					item.data.forEach((dataItem: any) => {
						if (dataItem as number) {
							return;
						}

						//eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
						dataItem.color = !ignoreChartColor
							? idx == 0
								? chartColor
								: chartColor2
							: undefined;
					});
				});
			} else {
				response.seriesData.forEach((item) => {
					item.color = undefined;
					item.data.forEach((dataItem: any) => {
						dimConfigValues.forEach((dimVal) => {
							const value = dimVal.values.find(
								// eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
								(val) => val.value === dataItem.name
							);
							if (value && value.color != '#000000') {
								// eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
								dataItem.color = value.color;
							}
						});
					});
				});
			}
		} else {
			if (!overrideValueDefinedColors) {
				//if multi-series dont update chart colors
			} else {
				response.seriesData.forEach((item) => {
					dimConfigValues.forEach((dimVal) => {
						const value = dimVal.values.find((val) => val.value === item.name);
						if (value && value.value && value.value != '' && value.color != '#000000') {
							// eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
							item.color = value.color;
						}
					});
				});
			}
		}
	}

	return response;
};

export const getPreviewDataAndCalculateGrandTotal = (
	dataSets: DraggableMenuItemData[],
	previewData: unknown[] | MultipartResponse<DataframeDataRetrievalResponse> | undefined,
	isChartView?: boolean,
	showAsPercentage?: boolean
): PreviewGrandTotal => {
	const grandTotal: [{ [key: string]: number }] = [{}];
	let previewDataAndTotal = <PreviewGrandTotal>{};
	let clone: MultipartResponse<DataframeDataRetrievalResponse> | undefined;

	if (isChartView) {
		dataSets?.forEach((fact) => {
			if (fact) {
				let factTotal = 0;

				(previewData as unknown[])?.forEach((col) => {
					const column = col as { [key: string]: string };

					if (fact.data) {
						factTotal += Number(column[fact.data.title.replace(/\s/g, '')]);
					}
				});

				if (fact.data) {
					grandTotal.push({ [`${fact.data.title.replaceAll(' ', '')}`]: factTotal });
				}
			}
		});
	} else {
		const data = previewData as MultipartResponse<DataframeDataRetrievalResponse> | undefined;
		clone = _.cloneDeep(data);

		dataSets?.forEach((fact) => {
			let factTotal = 0;
			let outerIndex = 0;
			let keyIndex = 0;

			data?.json?.columns?.forEach((col, outIndex) => {
				if (Array.isArray(col)) {
					col.forEach((innerCol, index) => {
						if (innerCol === fact.data?.title) {
							keyIndex = index;
							outerIndex = outIndex;
						}
					});
				}
			});

			data?.csvData?.forEach((dataItem) => {
				const splitItem = dataItem.split(/,(?=(?:(?:[^"]*"){2})*[^"]*$)/g);
				factTotal += +splitItem[keyIndex];
			});

			if (fact.data) {
				let columns = clone?.json?.columns[clone?.json?.columns.length - 1] ?? [];

				const percentCol = `${fact.data.title} Percent of Total`;

				if (
					showAsPercentage &&
					!columns.some((x) => x.toString().includes('Percent of Total'))
				) {
					// If not present, push it
					columns.push(percentCol);
				} else {
					const itemToRemove = columns.filter(
						(col) => !col.toString().includes('Percent of Total')
					);
					// If already present, remove it
					columns.splice(0, columns.length, ...itemToRemove);
				}
			}

			if (clone) {
				if (showAsPercentage) {
					clone.csvData = clone?.csvData?.map((dataItem) => {
						const splitItem = dataItem.split(/,(?=(?:(?:[^"]*"){2})*[^"]*$)/g);
						const calcValue = (+splitItem[keyIndex] / factTotal) * 100;

						return `${dataItem},${calcValue}`;
					});
				}

				if (fact.data) {
					grandTotal.push({ [`${fact.data.title.replaceAll(' ', '')}`]: factTotal });
				}
			}
		});
	}

	grandTotal.shift();

	previewDataAndTotal = {
		previewData: clone,
		grandTotal: grandTotal,
	};

	return previewDataAndTotal;
};

export const calculatePercentage = (
	fact?: string,
	grandTotal?: [{ [key: string]: number }],
	yTotal?: number | null
): number | null => {
	const total = grandTotal ? getNumberByKey(grandTotal, fact?.replaceAll(' ', '')) : 0;
	const value = yTotal ? (total > 0 ? ((yTotal ? Number(yTotal) : 0) / total) * 100 : 0) : null;

	return value;
};

export function getValueFromCsvByColumnName(data: any, columnName: string): string | null {
	const columns = data.json.columns[0]; // get first row of columns
	const targetIndex = columns.indexOf(columnName);

	if (targetIndex === -1) {
		return null; // Column not found
	}

	const firstRow = data.csvData[0];
	const values = firstRow.split(',');

	return values[targetIndex]?.replace(/^"|"$/g, '') ?? null;
}

export function detectDataType(value: string): 'string' | 'number' | 'date' | 'time' {
	// Trim and sanitize
	const trimmed = value.trim();

	// Check for number (includes whole, decimal, negative, and numbers with commas)
	const numberPattern = /^-?\d{1,3}(,\d{3})*(\.\d+)?$|^-?\d+(\.\d+)?$/;
	if (numberPattern.test(trimmed)) {
		return 'number';
	}

	// Check for time (HH:mm or HH:mm:ss, 24h format)
	const timePattern = /^([01]?\d|2[0-3]):[0-5]\d(:[0-5]\d)?$/;
	if (timePattern.test(trimmed)) {
		return 'time';
	}

	// Check for common date formats
	const datePatterns = [
		/^\d{4}-\d{2}-\d{2}$/, // YYYY-MM-DD
		/^\d{2}\/\d{2}\/\d{4}$/, // MM/DD/YYYY
		/^\d{2}-\d{2}-\d{4}$/, // MM-DD-YYYY
		/^\d{4}\/\d{2}\/\d{2}$/, // YYYY/MM/DD
		/^[A-Za-z]{3,9} \d{1,2}, \d{4}$/, // e.g., January 1, 2023
	];
	for (const pattern of datePatterns) {
		if (pattern.test(trimmed)) {
			return 'date';
		}
	}

	// Default to string
	return 'string';
}

export function isReallyEmpty(value: any): boolean {
	// Treat numbers (including 0, 0.0, NaN) as NOT empty
	if (typeof value === 'number') return false;

	// Null, undefined → empty
	if (value == null) return true;

	// Empty string → empty
	if (typeof value === 'string') return value.trim().length === 0;

	// Arrays → empty if length = 0
	if (Array.isArray(value)) return value.length === 0;

	// Objects → empty if no own keys
	if (typeof value === 'object') return Object.keys(value).length === 0;

	// Everything else (booleans, functions, symbols, etc.)
	return false;
}

export function adjustMonthsInRange(range: string, months: number, add: boolean): string {
	// Split original date range
	const [startStr, endStr] = range.split(' to ').map((s) => s.trim());

	// Parse dates
	const parse = (str: string) => {
		const [m, d, y] = str.split('/').map(Number);
		return new Date(y, m - 1, d);
	};

	let startDate = parse(startStr);
	let endDate = parse(endStr);

	// Apply month modification
	if (!add) {
		// subtract mode → modify START date only
		startDate.setMonth(startDate.getMonth() - months);
	} else {
		// add mode → modify END date only
		endDate.setMonth(endDate.getMonth() + months);
	}

	// Format function
	const fmt = (d: Date) =>
		[
			String(d.getMonth() + 1).padStart(2, '0'),
			String(d.getDate()).padStart(2, '0'),
			d.getFullYear(),
		].join('/');

	return `${fmt(startDate)} to ${fmt(endDate)}`;
}
